import { useState, useEffect } from 'react';
import { adminApi } from './AdminContext';
import { 
  ArrowLeft, Plus, Search, Phone, Mail, Globe, Building2, 
  Edit2, Trash2, X, User, FileText, ExternalLink
} from 'lucide-react';

const PARTNERSHIP_TYPES = [
  'General Contractor',
  'Subcontractor',
  'Aluminium Specialist',
  'Glass Specialist',
  'Electrical Contractor',
  'Plumbing Contractor',
  'HVAC Contractor',
  'Painting Contractor',
  'Flooring Contractor',
  'Cleaning Services',
  'Other'
];

const PartnersModule = ({ onBack, initialAction }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(initialAction === 'add');
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    logo_url: '',
    contact_person: '',
    phone: '',
    email: '',
    website: '',
    partnership_type: '',
    description: '',
    notes: ''
  });

  useEffect(() => {
    fetchPartners();
  }, [searchQuery, filterType]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (searchQuery) filters.search = searchQuery;
      if (filterType) filters.partnership_type = filterType;
      const data = await adminApi.getPartners(filters);
      setPartners(data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await adminApi.updatePartner(editingPartner.id, formData);
      } else {
        await adminApi.createPartner(formData);
      }
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await adminApi.deletePartner(id);
      fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      company_name: partner.company_name || '',
      logo_url: partner.logo_url || '',
      contact_person: partner.contact_person || '',
      phone: partner.phone || '',
      email: partner.email || '',
      website: partner.website || '',
      partnership_type: partner.partnership_type || '',
      description: partner.description || '',
      notes: partner.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingPartner(null);
    setFormData({
      company_name: '',
      logo_url: '',
      contact_person: '',
      phone: '',
      email: '',
      website: '',
      partnership_type: '',
      description: '',
      notes: ''
    });
  };

  const openWhatsApp = (phone) => {
    const cleaned = phone.replace(/[^0-9+]/g, '');
    window.open(`https://wa.me/${cleaned.replace('+', '')}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#060B14] text-white" data-testid="partners-module">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#060B14]/95 backdrop-blur-sm border-b border-white/10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-semibold">Partners</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#25D366] rounded-lg font-medium hover:bg-[#20BD5A] transition-colors"
            data-testid="add-partner-btn"
          >
            <Plus size={20} />
            Add Partner
          </button>
        </div>

        {/* Search and Filter */}
        <div className="px-4 pb-4 space-y-3">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by company, contact, phone, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-[#25D366]"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <button
              onClick={() => setFilterType('')}
              className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                !filterType ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
              }`}
            >
              All Types
            </button>
            {PARTNERSHIP_TYPES.slice(0, 6).map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filterType === type ? 'bg-[#25D366] text-white' : 'bg-white/10 text-slate-400'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Partners List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#25D366] border-t-transparent rounded-full"></div>
          </div>
        ) : partners.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Building2 size={48} className="mx-auto mb-4 opacity-50" />
            <p>No partners found</p>
            <p className="text-sm mt-1">Add your first partner to get started</p>
          </div>
        ) : (
          partners.map(partner => (
            <div 
              key={partner.id}
              className="bg-[#0A0F1C] border border-white/10 rounded-xl p-4"
              data-testid={`partner-card-${partner.id}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  {partner.logo_url ? (
                    <img 
                      src={partner.logo_url} 
                      alt={partner.company_name}
                      className="w-12 h-12 rounded-lg object-cover bg-white/10"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#25D366]/20 flex items-center justify-center">
                      <Building2 size={24} className="text-[#25D366]" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-white">{partner.company_name}</h3>
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">
                      {partner.partnership_type}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(partner)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    data-testid={`edit-partner-${partner.id}`}
                  >
                    <Edit2 size={16} className="text-slate-400" />
                  </button>
                  <button
                    onClick={() => handleDelete(partner.id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                    data-testid={`delete-partner-${partner.id}`}
                  >
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              </div>

              {/* Contact Person */}
              <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                <User size={14} />
                <span>{partner.contact_person}</span>
              </div>

              {/* Description */}
              {partner.description && (
                <p className="text-slate-400 text-sm mb-3 line-clamp-2">
                  {partner.description}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={() => openWhatsApp(partner.phone)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366]/20 text-[#25D366] rounded-lg text-sm hover:bg-[#25D366]/30 transition-colors"
                >
                  <Phone size={14} />
                  WhatsApp
                </button>
                <a
                  href={`tel:${partner.phone}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors"
                >
                  <Phone size={14} />
                  Call
                </a>
                {partner.email && (
                  <a
                    href={`mailto:${partner.email}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors"
                  >
                    <Mail size={14} />
                    Email
                  </a>
                )}
                {partner.website && (
                  <a
                    href={partner.website.startsWith('http') ? partner.website : `https://${partner.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors"
                  >
                    <Globe size={14} />
                    Website
                  </a>
                )}
              </div>

              {/* Notes */}
              {partner.notes && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="flex items-start gap-2 text-slate-500 text-xs">
                    <FileText size={12} className="mt-0.5" />
                    <span>{partner.notes}</span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center">
          <div className="bg-[#0A0F1C] w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
            <div className="sticky top-0 bg-[#0A0F1C] border-b border-white/10 p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">
                {editingPartner ? 'Edit Partner' : 'Add New Partner'}
              </h2>
              <button 
                onClick={resetForm}
                className="p-2 hover:bg-white/10 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Company Name *</label>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="ABC Contractors LLC"
                  data-testid="partner-company-name"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Contact Person *</label>
                <input
                  type="text"
                  required
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="John Smith"
                  data-testid="partner-contact-person"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="+971 50 123 4567"
                  data-testid="partner-phone"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="contact@company.com"
                  data-testid="partner-email"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Website</label>
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="www.company.com"
                  data-testid="partner-website"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Logo URL</label>
                <input
                  type="text"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  placeholder="https://example.com/logo.png"
                  data-testid="partner-logo-url"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Partnership Type *</label>
                <select
                  required
                  value={formData.partnership_type}
                  onChange={(e) => setFormData({...formData, partnership_type: e.target.value})}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366]"
                  data-testid="partner-type"
                >
                  <option value="">Select type</option>
                  {PARTNERSHIP_TYPES.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366] resize-none"
                  placeholder="Brief description of services..."
                  data-testid="partner-description"
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#25D366] resize-none"
                  placeholder="Internal notes..."
                  data-testid="partner-notes"
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-[#25D366] rounded-lg font-semibold hover:bg-[#20BD5A] transition-colors"
                data-testid="save-partner-btn"
              >
                {editingPartner ? 'Update Partner' : 'Add Partner'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersModule;
